import { backfillCareIndustryNavigation } from './landingEditorCareNavigationMigration.js';
import { backfillRetailIndustryNavigation } from './landingEditorRetailNavigationMigration.js';
import { refreshIndustryHeroHighlight } from './landingEditorIndustryThreeLineHeadingsMigration.js';
import { industryHeroIntro } from './landingEditorIndustryIntrosMigration.js';
import { industryHeroPresentation } from '../content/industryHeroPresentation.js';
import { applyBranchPortalLinks } from '../content/industryPageActions.js';
import { contactMapConsentCopy } from '../content/contactMapContent.js';
import { industryHeroHeading } from './landingEditorIndustryHeadingsMigration.js';
import { migrateCompanyPeopleHeroContent, migrateCompanyPeopleHeroVisibility } from './landingEditorCompanyPeopleHeroMigration.js';
import { collectRetainedFields, type RetainedField } from '@adornis/web-editor/migrations/retained-fields.js';
import { ensureMissionFluidLayout, normalizeFluidSize } from './landingEditorFluidLayout.js';
import { applicationPortal, type LandingContent, type LandingLocale, landingPageContentRepository } from '../content/landingContent.js';
import {
  localizeContactPage,
  localizeIndustryHeroPage,
  type StaticPageAudience,
  type StaticPageContent,
  type StaticPageKey,
  staticPageContentRepository,
  staticPageKeys,
} from '../content/staticPageContent.js';
import { localizeStaticPage } from '../content/staticPageLocalization.js';
import { industryPageKeys } from '../content/industryPages.js';
import { industryApplicationHref } from '../content/industryPageContent.js';
import {
  clampTypographySize,
  isTypographyStyleKey,
  normalizeTypographyScale,
} from './landingEditorTypography.js';
import {
  normalizeInlineTextSources,
  normalizeInlineTextStyles,
} from './landingEditorInlineTypography.js';
import {
  clampEditorGridColumns, clampEditorImageScale,
  normalizeEditorFocalPoint, normalizeEditorGridPlacement, normalizeEditorImageScaleMap,
} from './landingEditorGeometry.js';
import {
  normalizeAutoLayoutFrame,
  normalizeAutoLayoutItem,
  repairInvalidGlobusAutoLayout,
} from './landingEditorAutoLayout.js';
import { landingEditorProjectInput } from './landingEditorWebsiteDocument.js';
import { normalizeLandingEditorCommentAnchorIds } from './landingEditorComments.js';
import { repairStaticHeroLayoutFrames } from './landingEditorStaticHeroMigration.js';
import { migrateFooterLinks, remapFooterKeys } from './landingEditorFooterMigration.js';
import { migrateRetiredLandingLinks } from './landingEditorRetiredLinks.js';
import { refreshCompanyPlatformHeroCopy } from './landingEditorCompanyPlatformHeroMigration.js';
import { migrateCompanyHeroActionLink, refreshCompanyJourneyCopy } from './landingEditorCompanyJourneyMigration.js';
import { refreshMatchingDemoPortraits } from './landingEditorMatchingPortraitMigration.js';
export type { LandingEditorFocalPoint, LandingEditorGridPlacement } from './landingEditorGeometry.js'; export type { LandingEditorAutoLayoutFrame, LandingEditorAutoLayoutItem } from './landingEditorAutoLayout.js';
// Sections that make up the default landing page as the general Viva Porta company hub.
export const landingDefaultSectionKeys = ['globusCampaignApplicant', 'companyPeopleHero', 'companyObjections', 'companyMatching', 'globusPartner', 'intro', 'industryOverview', 'laborShortage', 'process', 'values', 'partners', 'story', 'quality', 'contact'] as const;
// Additional sections that are not part of the default page but can be added through the editor (drag & drop).
export const landingOptionalSectionKeys = [
  'companyPlatformHero',
  'hero',
  'heroApplicant',
  'heroCompany',
  'heroFanApplicant',
  'heroFanCompany',
  'globusCampaignCompany',
  'reasons',
  'requirements',
  'benefits',
  'costs',
  'recognition',
  'faqApplicant',
  'faqCompany',
  'knowledge',
  'info',
  'team',
  'consultationApplicant',
  'consultationCompany',
] as const;
export type LandingHomeSectionKey = (typeof landingDefaultSectionKeys)[number] | (typeof landingOptionalSectionKeys)[number];

export const landingHomeSectionKeys: readonly LandingHomeSectionKey[] = [...landingDefaultSectionKeys, ...landingOptionalSectionKeys];

export const landingCompanyHeroSectionKeys: readonly LandingHomeSectionKey[] = ['companyPeopleHero', 'companyPlatformHero', 'heroCompany', 'heroFanCompany', 'globusCampaignCompany']; export const isLandingCompanyHeroSection = (section: LandingHomeSectionKey) => landingCompanyHeroSectionKeys.includes(section);

export type { WebEditorElementFrame as LandingEditorElementFrame, WebEditorProjectSettings as LandingEditorProjectSettings, WebEditorTestimonialColumns as LandingEditorTestimonialColumns } from '@adornis/web-editor/layout.js';
import type { WebEditorElementFrame as LandingEditorElementFrame, WebEditorProjectSettings as LandingEditorProjectSettings } from '@adornis/web-editor/layout.js';
export interface LandingEditorProject {
  retainedFields?: RetainedField[];
  version: 1;
  contentRevision: 29;
  updatedAt: string;
  publishedAt?: string;
  content: Record<LandingLocale, LandingContent>;
  staticPages: Record<
    LandingLocale,
    Record<StaticPageAudience, Record<StaticPageKey, StaticPageContent>>
  >;
  sectionOrder: LandingHomeSectionKey[];
  // Modular landing sections that have been added to a specific static sub-page (drag & drop from the assets palette).
  staticSectionOrder: Record<StaticPageAudience, Record<StaticPageKey, LandingHomeSectionKey[]>>;
  // Built-in sections of a static page (hero, cards, team, contact, page-specific content blocks).
  staticPageSectionOrder: Record<StaticPageAudience, Record<StaticPageKey, StaticPageBuiltInSectionKey[]>>;
  elementFrames: Record<string, LandingEditorElementFrame>;
  editorAnchorIds: Record<string, string>;
  settings?: LandingEditorProjectSettings;
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

export const cloneEditorValue = <T>(value: T): T => {
  try {
    return JSON.parse(JSON.stringify(value)) as T;
  } catch (cause) {
    throw new Error('Editor content must be JSON-serializable.', { cause });
  }
};

const normalizeSiteTitleSetting = (value: string): string =>
  value.replace(/\bViva\s*Porta[\s-]*Medical\b/giu, 'Viva Porta GmbH');

const mergeDeep = <T>(fallback: T, incoming: unknown): T => {
  if (Array.isArray(fallback)) return (Array.isArray(incoming) ? incoming : fallback) as T;
  if (!isRecord(fallback)) return (incoming === undefined || incoming === null ? fallback : incoming) as T;
  if (!isRecord(incoming)) return fallback;

  const merged: Record<string, unknown> = { ...fallback };
  for (const [key, fallbackValue] of Object.entries(fallback)) {
    merged[key] = mergeDeep(fallbackValue, incoming[key]);
  }
  return merged as T;
};

const normalizeSectionOrder = (incoming: unknown, contentRevision = 0): LandingHomeSectionKey[] => {
  const sections = Array.isArray(incoming) ? incoming : [];
  const known = new Set<LandingHomeSectionKey>(landingHomeSectionKeys);
  const ordered = sections.filter((section): section is LandingHomeSectionKey => known.has(section as LandingHomeSectionKey)).filter(section => !disabledLandingSections.has(section));
  if (!ordered.length) return [...landingDefaultSectionKeys];
  const hasHubStructure = ordered.includes('industryOverview') || ordered.includes('laborShortage');
  if (!hasHubStructure) return [...landingDefaultSectionKeys];
  const landingHero = 'globusCampaignApplicant' as const;
  const isLegacyCampaignOrder = contentRevision < 11;
  const requiresTestimonialMove = contentRevision < 12;
  const requiresCompanyPlatformHeroMigration = contentRevision < 16;
  const mappedOrder = ordered.filter(section => section !== 'hero');
  const withoutLegacyDefaultHero = isLegacyCampaignOrder
    ? mappedOrder.filter(section => section !== 'heroFanApplicant' && section !== landingHero)
    : mappedOrder;
  const hasEditableHero = withoutLegacyDefaultHero.includes(landingHero) || withoutLegacyDefaultHero.includes('heroFanApplicant');
  let nextOrder: LandingHomeSectionKey[] = hasEditableHero ? [...withoutLegacyDefaultHero] : [landingHero, ...withoutLegacyDefaultHero];
  if (isLegacyCampaignOrder) nextOrder = [landingHero, ...nextOrder.filter(section => section !== landingHero)];
  // Legacy projects did not contain the Globus section yet. Insert Globus and the existing
  // testimonial section below the hero once; as soon as the editor saves an order containing
  // Globus, that explicit order is preserved and both sections remain freely movable.
  if (!nextOrder.includes('globusPartner') || isLegacyCampaignOrder) {
    const withoutStory = nextOrder.filter(section => section !== 'story');
    const withoutGlobus = withoutStory.filter(section => section !== 'globusPartner');
    const heroIndex = withoutGlobus.findIndex(section => section === landingHero || section === 'heroFanApplicant');
    const insertAt = heroIndex >= 0 ? heroIndex + 1 : 0;
    nextOrder = [...withoutGlobus.slice(0, insertAt), 'globusPartner', 'story', ...withoutGlobus.slice(insertAt)];
  }
  // The partner matrix shipped after the first projects were published. Add it near the existing
  // testimonial for projects that already have a stored order; the revision-12 migration below then
  // establishes the final partner-matrix → testimonial sequence once.
  if (!nextOrder.includes('partners')) {
    const storyIndex = nextOrder.indexOf('story');
    if (storyIndex >= 0) nextOrder = [...nextOrder.slice(0, storyIndex + 1), 'partners', ...nextOrder.slice(storyIndex + 1)];
  }
  if (requiresTestimonialMove && nextOrder.includes('partners') && nextOrder.includes('story')) {
    const withoutStory = nextOrder.filter(section => section !== 'story');
    const partnersIndex = withoutStory.indexOf('partners');
    nextOrder = [
      ...withoutStory.slice(0, partnersIndex + 1),
      'story',
      ...withoutStory.slice(partnersIndex + 1),
    ];
  }
  if (requiresCompanyPlatformHeroMigration) {
    const previousCompanyHeroIndex = nextOrder.findIndex(section => landingCompanyHeroSectionKeys.includes(section)); nextOrder = nextOrder.filter(section => !landingCompanyHeroSectionKeys.includes(section));
    const applicantHeroIndex = nextOrder.findIndex(section => section === landingHero || section === 'heroFanApplicant');
    nextOrder.splice(previousCompanyHeroIndex >= 0 ? Math.min(previousCompanyHeroIndex, nextOrder.length) : applicantHeroIndex >= 0 ? applicantHeroIndex + 1 : 0, 0, 'companyPlatformHero');
  } else {
    let foundCompanyHero = false;
    nextOrder = nextOrder.filter(section => {
      if (!landingCompanyHeroSectionKeys.includes(section)) return true;
      if (foundCompanyHero) return false;
      foundCompanyHero = true;
      return true;
    });
  }
  if (contentRevision < 20 && nextOrder.includes('companyPlatformHero')) {
    const heroIndex = nextOrder.indexOf('companyPlatformHero');
    const additions = (['companyObjections', 'companyMatching'] as const).filter(section => !nextOrder.includes(section));
    nextOrder.splice(heroIndex + 1, 0, ...additions);
  }
  if (contentRevision < 22) nextOrder = nextOrder.map(section => section === 'companyPlatformHero' ? 'companyPeopleHero' : section);
  const hasAnyHero = nextOrder.includes(landingHero) || nextOrder.includes('heroFanApplicant');
  const hasCompanyHero = nextOrder.some(section => landingCompanyHeroSectionKeys.includes(section));
  return [
    ...nextOrder,
    ...landingDefaultSectionKeys.filter(
      section =>
        !nextOrder.includes(section) &&
        section !== 'companyObjections' && section !== 'companyMatching' &&
        (section !== landingHero || !hasAnyHero) &&
        (section !== 'companyPeopleHero' || !hasCompanyHero),
    ),
  ];
};

// Static sub-pages start without any modular sections; only the keys an editor explicitly added are kept.
const normalizeStaticSectionList = (incoming: unknown): LandingHomeSectionKey[] => {
  const sections = Array.isArray(incoming) ? incoming : [];
  const known = new Set<LandingHomeSectionKey>(landingHomeSectionKeys);
  return sections.filter((section): section is LandingHomeSectionKey => known.has(section as LandingHomeSectionKey)).filter(section => !disabledLandingSections.has(section));
};

export const disabledLandingSections = new Set<LandingHomeSectionKey>([
  'consultationApplicant',
  'consultationCompany',
  'team',
]);

export const staticPageAudiences: readonly StaticPageAudience[] = ['applicant', 'company'];

export type StaticPageBuiltInSectionKey = 'hero' | 'missionStory' | 'cards' | 'globusJobs' | 'faq' | 'cta' | 'team' | 'contact' | `detail:${number}`;

export const getDefaultStaticPageBuiltInSectionOrder = (page: StaticPageContent): StaticPageBuiltInSectionKey[] => {
  const order: StaticPageBuiltInSectionKey[] = ['hero'];
  if (page.key === 'globus' && page.globusJobs?.length) order.push('globusJobs');
  if (page.missionStory) order.push('missionStory');
  if (page.cards.length && page.key !== 'kontakt') order.push('cards');
  if (page.key === 'globus' && !page.globusJobs?.length && page.cta) order.push('cta');
  (page.sections ?? []).forEach((_, index) => {
    order.push(`detail:${index}`);
  });
  if (page.globusJobs?.length && !order.includes('globusJobs')) order.push('globusJobs');
  if (page.faq?.length) order.push('faq');
  if (page.cta && !order.includes('cta')) order.push('cta');
  if (page.team?.length) order.push('team');
  if (page.contact) order.push('contact');
  return order;
};

const normalizeStaticPageBuiltInSectionOrder = (incoming: unknown, page: StaticPageContent): StaticPageBuiltInSectionKey[] => {
  const defaults = getDefaultStaticPageBuiltInSectionOrder(page);
  const known = new Set<StaticPageBuiltInSectionKey>(defaults);
  const ordered = Array.isArray(incoming) ? incoming.filter((section): section is StaticPageBuiltInSectionKey => known.has(section as StaticPageBuiltInSectionKey)) : [];
  const normalized: StaticPageBuiltInSectionKey[] = [...ordered, ...defaults.filter(section => !ordered.includes(section))];
  if (page.key === 'globus' && page.globusJobs?.length) {
    const withoutGlobusJobs: StaticPageBuiltInSectionKey[] = normalized.filter(section => section !== 'globusJobs');
    const heroIndex = withoutGlobusJobs.indexOf('hero');
    withoutGlobusJobs.splice(heroIndex >= 0 ? heroIndex + 1 : 1, 0, 'globusJobs');
    return withoutGlobusJobs;
  }
  if (page.key === 'globus' && !page.globusJobs?.length && page.cta) {
    const withoutCta: StaticPageBuiltInSectionKey[] = normalized.filter(section => section !== 'cta');
    const cardsIndex = withoutCta.indexOf('cards');
    const heroIndex = withoutCta.indexOf('hero');
    withoutCta.splice(cardsIndex >= 0 ? cardsIndex + 1 : heroIndex >= 0 ? heroIndex + 1 : 1, 0, 'cta');
    return withoutCta;
  }
  if (page.missionStory) {
    const withoutMissionStory: StaticPageBuiltInSectionKey[] = normalized.filter(section => section !== 'missionStory');
    const heroIndex = withoutMissionStory.indexOf('hero');
    withoutMissionStory.splice(heroIndex >= 0 ? heroIndex + 1 : 1, 0, 'missionStory');
    return withoutMissionStory;
  }
  return normalized;
};

// Hero-fan reverse-side content was introduced after the first projects were published, so card arrays
// (which mergeDeep replaces wholesale) can lack those fields. Backfill them from the code defaults so
// the reverse side renders and the editor can show/edit the copy instead of empty fields.
import {
  backfillCompanyHeroFan,
  backfillFanTestimonials,
  backfillFeaturedGroupPhoto,
  backfillGlobusBakeryMedia,
  backfillGlobusButcherMedia,
  backfillGlobusFreshCounterMedia,
  backfillGlobusGastronomyMedia,
  backfillIndustryHeroFan,
  backfillLandingTeamMembers,
  backfillStaticPageDefaults,
  backfillTeamMemberLinks,
  hasLegacyLocalizedLandingContent,
  normalizeGlobusPartnerContent,
  normalizeLaborShortageCopy,
  normalizeLandingPresentation,
  normalizeMissionLandingCopy,
  normalizePartnerNetworkContent,
  normalizeStoryContent,
  normalizeStaticPagePresentation,
  normalizeVisibleIndustries,
  removeProcessMedia,
  softenLegacyQualityClaims,
  syncFanCardImages,
} from './landingEditorContentMigrations.js';
import { refreshBuiltInGlobusJobAssets, repairInvalidEditorAssets } from './landingEditorAssetMigration.js';
import { migrateLegacyPhoneNumber } from './landingEditorPhoneMigration.js';
const emptyStaticSectionOrder = (): Record<StaticPageKey, LandingHomeSectionKey[]> => {
  const order = {} as Record<StaticPageKey, LandingHomeSectionKey[]>;
  for (const pageKey of staticPageKeys) order[pageKey] = [];
  return order;
};

// Modular sections that ship by default on the static sub-pages so a fresh project already matches the sitemap.
// The "Für Arbeitgeber" page (/unternehmen) keeps its static hero + Leistungen cards and appends these below;
// "Über Viva Porta" gets the values/knowledge blocks. Existing published projects extend these via drag & drop.
const staticSectionDefaults: Partial<Record<StaticPageKey, LandingHomeSectionKey[]>> = {
  unternehmen: ['process', 'quality', 'faqCompany'],
  pflege: ['process', 'quality', 'faqCompany'],
  gastro: ['process', 'quality', 'faqCompany'],
  handwerk: ['process', 'quality', 'faqCompany'],
  einzelhandel: ['process', 'quality', 'faqCompany'],
  ablauf: ['quality', 'faqCompany'],
  'ueber-vivaporta': ['values', 'quality'],
  team: ['quality'],
  kontakt: [],
};

const applicantStaticSectionDefaults: Partial<Record<StaticPageKey, LandingHomeSectionKey[]>> = {
  unternehmen: ['faqApplicant'],
  pflege: ['faqApplicant'],
  gastro: ['faqApplicant'],
  handwerk: ['faqApplicant'],
  einzelhandel: ['faqApplicant'],
  ablauf: ['requirements', 'faqApplicant'],
  'ueber-vivaporta': ['values', 'knowledge'],
  kontakt: [],
};

const deprecatedStaticSections: Partial<Record<StaticPageKey, LandingHomeSectionKey[]>> = {
  'ueber-vivaporta': ['knowledge'],
};

const defaultStaticSectionOrderForAudience = (audience: StaticPageAudience): Record<StaticPageKey, LandingHomeSectionKey[]> => {
  const order = emptyStaticSectionOrder();
  const defaults = audience === 'company' ? staticSectionDefaults : applicantStaticSectionDefaults;
  for (const pageKey of staticPageKeys) order[pageKey] = [...(defaults[pageKey] ?? [])];
  return order;
};

const defaultStaticSectionOrder = (): Record<StaticPageAudience, Record<StaticPageKey, LandingHomeSectionKey[]>> => ({
  applicant: defaultStaticSectionOrderForAudience('applicant'),
  company: defaultStaticSectionOrderForAudience('company'),
});

const defaultStaticPageBuiltInSectionOrder = (staticPages: Record<StaticPageAudience, Record<StaticPageKey, StaticPageContent>>): Record<StaticPageAudience, Record<StaticPageKey, StaticPageBuiltInSectionKey[]>> => {
  const order = {} as Record<StaticPageAudience, Record<StaticPageKey, StaticPageBuiltInSectionKey[]>>;
  for (const audience of staticPageAudiences) {
    order[audience] = {} as Record<StaticPageKey, StaticPageBuiltInSectionKey[]>;
    for (const pageKey of staticPageKeys) {
      order[audience][pageKey] = getDefaultStaticPageBuiltInSectionOrder(staticPages[audience][pageKey]);
    }
  }
  return order;
};

const normalizeStaticSectionOrderForAudience = (incoming: unknown, audience: StaticPageAudience): Record<StaticPageKey, LandingHomeSectionKey[]> => {
  const order = emptyStaticSectionOrder();
  if (!isRecord(incoming)) return defaultStaticSectionOrderForAudience(audience);
  const defaults = audience === 'company' ? staticSectionDefaults : applicantStaticSectionDefaults;
  for (const pageKey of staticPageKeys) {
    const deprecated = deprecatedStaticSections[pageKey] ?? [];
    if (Object.hasOwn(incoming, pageKey)) {
      order[pageKey] = normalizeStaticSectionList(incoming[pageKey]).filter(section => !deprecated.includes(section) && !(pageKey === 'kontakt' && (section === 'faqApplicant' || section === 'faqCompany')));
      continue;
    }
    order[pageKey] = [...(defaults[pageKey] ?? [])].filter(section => !deprecated.includes(section));
  }
  return order;
};

const normalizeStaticSectionOrder = (incoming: unknown): Record<StaticPageAudience, Record<StaticPageKey, LandingHomeSectionKey[]>> => {
  if (!isRecord(incoming)) return defaultStaticSectionOrder();
  const looksNested = staticPageAudiences.some(audience => isRecord(incoming[audience]));
  if (!looksNested) {
    return {
      applicant: defaultStaticSectionOrderForAudience('applicant'),
      company: normalizeStaticSectionOrderForAudience(incoming, 'company'),
    };
  }
  return {
    applicant: normalizeStaticSectionOrderForAudience(incoming.applicant, 'applicant'),
    company: normalizeStaticSectionOrderForAudience(incoming.company, 'company'),
  };
};

const normalizeStaticPageBuiltInSectionOrderMap = (incoming: unknown, staticPages: Record<StaticPageAudience, Record<StaticPageKey, StaticPageContent>>): Record<StaticPageAudience, Record<StaticPageKey, StaticPageBuiltInSectionKey[]>> => {
  const incomingRecord = isRecord(incoming) ? incoming : {};
  const order = {} as Record<StaticPageAudience, Record<StaticPageKey, StaticPageBuiltInSectionKey[]>>;
  for (const audience of staticPageAudiences) {
    order[audience] = {} as Record<StaticPageKey, StaticPageBuiltInSectionKey[]>;
    const audienceRecord = isRecord(incomingRecord[audience]) ? incomingRecord[audience] : {};
    for (const pageKey of staticPageKeys) {
      order[audience][pageKey] = normalizeStaticPageBuiltInSectionOrder(audienceRecord[pageKey], staticPages[audience][pageKey]);
    }
  }
  return order;
};

const normalizeSettings = (incoming: unknown): LandingEditorProjectSettings => {
  if (typeof incoming !== 'object' || incoming === null) return {};
  const obj = incoming as Record<string, unknown>;
  return {
    ...(typeof obj.siteTitle === 'string' ? { siteTitle: normalizeSiteTitleSetting(obj.siteTitle) } : {}),
    ...(typeof obj.seoDescription === 'string' ? { seoDescription: obj.seoDescription } : {}),
    ...(typeof obj.faviconUrl === 'string' ? { faviconUrl: obj.faviconUrl } : {}),
    ...(typeof obj.cursorCss === 'string' ? { cursorCss: obj.cursorCss } : {}),
    ...(typeof obj.cursorEffect === 'string' ? { cursorEffect: obj.cursorEffect } : {}),
    ...(typeof obj.cursorColor === 'string' ? { cursorColor: obj.cursorColor } : {}),
    ...(Object.keys(normalizeTypographyScale(obj.typography)).length
      ? { typography: normalizeTypographyScale(obj.typography) }
      : {}),
  };
};

const normalizeFrames = (incoming: unknown): Record<string, LandingEditorElementFrame> => {
  if (!isRecord(incoming)) return {};

  const frames: Record<string, LandingEditorElementFrame> = {};
  for (const [id, frame] of Object.entries(incoming)) {
    if (!isRecord(frame)) continue;
    const x = typeof frame.x === 'number' && Number.isFinite(frame.x) ? frame.x : 0;
    const y = typeof frame.y === 'number' && Number.isFinite(frame.y) ? frame.y : 0;
    const zIndex = typeof frame.zIndex === 'number' && Number.isFinite(frame.zIndex) ? frame.zIndex : undefined;
    const width = typeof frame.width === 'number' && Number.isFinite(frame.width) ? frame.width : undefined;
    const height = typeof frame.height === 'number' && Number.isFinite(frame.height) ? frame.height : undefined;
    const opacity = typeof frame.opacity === 'number' && Number.isFinite(frame.opacity) ? Math.min(1, Math.max(0, frame.opacity)) : undefined;
    const borderRadius = typeof frame.borderRadius === 'number' && Number.isFinite(frame.borderRadius) ? frame.borderRadius : undefined;
    const fontWeight = typeof frame.fontWeight === 'number' && Number.isFinite(frame.fontWeight) ? frame.fontWeight : undefined;
    const fontSizeDesktop = typeof frame.fontSizeDesktop === 'number' && Number.isFinite(frame.fontSizeDesktop) ? clampTypographySize(frame.fontSizeDesktop) : undefined;
    const fontSizeMobile = typeof frame.fontSizeMobile === 'number' && Number.isFinite(frame.fontSizeMobile) ? clampTypographySize(frame.fontSizeMobile) : undefined;
    const typographyStyle = isTypographyStyleKey(frame.typographyStyle) ? frame.typographyStyle : undefined;
    const inlineTextStyles = normalizeInlineTextStyles(frame.inlineTextStyles);
    const inlineTextSources = normalizeInlineTextSources(frame.inlineTextSources);
    const textAlign = frame.textAlign === 'left' || frame.textAlign === 'center' || frame.textAlign === 'right' ? frame.textAlign : undefined;
    const objectFit = frame.objectFit === 'cover' || frame.objectFit === 'contain' || frame.objectFit === 'fill' ? frame.objectFit : undefined;
    const testimonialColumns =
      frame.testimonialColumns === 1 ||
      frame.testimonialColumns === 2 ||
      frame.testimonialColumns === 3 ||
      frame.testimonialColumns === 4
        ? frame.testimonialColumns
        : undefined;
    const gridColumnsDesktop = typeof frame.gridColumnsDesktop === 'number' && Number.isFinite(frame.gridColumnsDesktop)
      ? clampEditorGridColumns(frame.gridColumnsDesktop)
      : undefined;
    const gridColumnsMobile = typeof frame.gridColumnsMobile === 'number' && Number.isFinite(frame.gridColumnsMobile)
      ? clampEditorGridColumns(frame.gridColumnsMobile)
      : undefined;
    const gridDesktop = normalizeEditorGridPlacement(frame.gridDesktop, gridColumnsDesktop);
    const gridMobile = normalizeEditorGridPlacement(frame.gridMobile, gridColumnsMobile);
    const imageFocusDesktop = normalizeEditorFocalPoint(frame.imageFocusDesktop);
    const imageFocusMobile = normalizeEditorFocalPoint(frame.imageFocusMobile);
    const imageScaleDesktop = typeof frame.imageScaleDesktop === 'number' && Number.isFinite(frame.imageScaleDesktop) ? clampEditorImageScale(frame.imageScaleDesktop) : undefined;
    const imageScaleMobile = typeof frame.imageScaleMobile === 'number' && Number.isFinite(frame.imageScaleMobile) ? clampEditorImageScale(frame.imageScaleMobile) : undefined;
    const imageScaleByBreakpoint = normalizeEditorImageScaleMap(frame.imageScaleByBreakpoint);
    const autoLayoutDesktop = normalizeAutoLayoutFrame(frame.autoLayoutDesktop);
    const autoLayoutMobile = normalizeAutoLayoutFrame(frame.autoLayoutMobile);
    const autoLayoutItemDesktop = normalizeAutoLayoutItem(frame.autoLayoutItemDesktop, autoLayoutDesktop?.columns);
    const autoLayoutItemMobile = normalizeAutoLayoutItem(frame.autoLayoutItemMobile, autoLayoutMobile?.columns);
    frames[id] = {
      x,
      y,
      ...(typeof frame.fluidLayout === 'boolean' ? { fluidLayout: frame.fluidLayout } : {}), ...(normalizeFluidSize(frame.fluidSize) ? { fluidSize: normalizeFluidSize(frame.fluidSize) } : {}),
      hidden: frame.hidden === true,
      ...(zIndex !== undefined ? { zIndex } : {}),
      ...(width !== undefined ? { width } : {}),
      ...(height !== undefined ? { height } : {}),
      ...(opacity !== undefined ? { opacity } : {}),
      ...(typeof frame.color === 'string' ? { color: frame.color } : {}),
      ...(typeof frame.backgroundColor === 'string' ? { backgroundColor: frame.backgroundColor } : {}),
      ...(fontWeight !== undefined ? { fontWeight } : {}),
      ...(typographyStyle ? { typographyStyle } : {}),
      ...(fontSizeDesktop !== undefined ? { fontSizeDesktop } : {}),
      ...(fontSizeMobile !== undefined ? { fontSizeMobile } : {}),
      ...(Object.keys(inlineTextStyles).length ? { inlineTextStyles } : {}),
      ...(Object.keys(inlineTextSources).length ? { inlineTextSources } : {}),
      ...(textAlign !== undefined ? { textAlign } : {}),
      ...(borderRadius !== undefined ? { borderRadius } : {}),
      ...(objectFit !== undefined ? { objectFit } : {}),
      ...(testimonialColumns !== undefined ? { testimonialColumns } : {}),
      ...(frame.gridContainerDesktop === true && gridColumnsDesktop !== undefined ? { gridContainerDesktop: true } : {}),
      ...(frame.gridContainerMobile === true && gridColumnsMobile !== undefined ? { gridContainerMobile: true } : {}),
      ...(gridColumnsDesktop !== undefined ? { gridColumnsDesktop } : {}),
      ...(gridColumnsMobile !== undefined ? { gridColumnsMobile } : {}),
      ...(gridDesktop ? { gridDesktop } : {}),
      ...(gridMobile ? { gridMobile } : {}),
      ...(imageFocusDesktop ? { imageFocusDesktop } : {}),
      ...(imageFocusMobile ? { imageFocusMobile } : {}),
      ...(imageScaleDesktop !== undefined ? { imageScaleDesktop } : {}), ...(imageScaleMobile !== undefined ? { imageScaleMobile } : {}),
      ...(imageScaleByBreakpoint ? { imageScaleByBreakpoint } : {}),
      ...(typeof frame.imageMaskDesktop === 'boolean' ? { imageMaskDesktop: frame.imageMaskDesktop } : {}), ...(typeof frame.imageMaskMobile === 'boolean' ? { imageMaskMobile: frame.imageMaskMobile } : {}),
      ...(autoLayoutDesktop ? { autoLayoutDesktop } : {}),
      ...(autoLayoutMobile ? { autoLayoutMobile } : {}),
      ...(autoLayoutItemDesktop ? { autoLayoutItemDesktop } : {}),
      ...(autoLayoutItemMobile ? { autoLayoutItemMobile } : {}),
      ...(typeof frame.href === 'string' && frame.href.trim() ? { href: frame.href.trim() } : {}),
    };
  }

  // Hero-fan card labels (eyebrow + title) are anchored to the bottom of each card and must line up
  // with one another. A stray drag offset on a single label (e.g. the applicant Pflege title) would
  // shift it out of alignment with its eyebrow, so we strip any manual translate from these labels and
  // keep them on their CSS position — consistent across every hero-fan card.
  const heroFanLabelPattern = /^heroFan(Applicant|Company)\.card\.\d+\.(title|eyebrow)$/;
  for (const [id, frame] of Object.entries(frames)) {
    if (heroFanLabelPattern.test(id) && (frame.x || frame.y)) {
      frames[id] = { ...frame, x: 0, y: 0 };
    }
  }

  return ensureMissionFluidLayout(repairInvalidGlobusAutoLayout(repairStaticHeroLayoutFrames(frames)));
};

export const createDefaultLandingEditorProject = (): LandingEditorProject => {
  const content = {} as Record<LandingLocale, LandingContent>;
  for (const locale of landingPageContentRepository.locales) {
    content[locale] = cloneEditorValue(landingPageContentRepository.getContent(locale));
  }
  const staticPages = {} as LandingEditorProject['staticPages'];
  for (const locale of landingPageContentRepository.locales) {
    staticPages[locale] = {} as Record<StaticPageAudience, Record<StaticPageKey, StaticPageContent>>;
    for (const audience of staticPageAudiences) {
      staticPages[locale][audience] = {} as Record<StaticPageKey, StaticPageContent>;
      for (const pageKey of staticPageKeys) {
        const source = staticPageContentRepository.getPage(pageKey, audience);
        const contactLocalized = localizeContactPage(source, audience, locale);
        const pageLocalized = localizeStaticPage(
          contactLocalized,
          audience,
          locale,
          content[locale].team.members,
        );
        staticPages[locale][audience][pageKey] = cloneEditorValue(
          industryHeroPresentation(localizeIndustryHeroPage(pageLocalized, pageKey, audience, locale), locale),
        );
        applyBranchPortalLinks(staticPages[locale][audience][pageKey], pageKey, locale, audience);
      }
    }
  }
  const project: LandingEditorProject = {
    version: 1,
    contentRevision: 29,
    updatedAt: new Date().toISOString(),
    content,
    staticPages,
    sectionOrder: [...landingDefaultSectionKeys],
    staticSectionOrder: defaultStaticSectionOrder(),
    staticPageSectionOrder: defaultStaticPageBuiltInSectionOrder(staticPages.de),
    elementFrames: ensureMissionFluidLayout({}),
    editorAnchorIds: {},
    settings: {},
  };
  migrateRetiredLandingLinks(project);
  return project;
};

// Der Live-Inhalt kommt aus der DB-Veröffentlichung und enthält noch alte CTA-Ziele. Da das
// Bewerberportal auf einer anderen Subdomain liegt, erzwingen wir die korrekten (zur Laufzeit
// host-abgeleiteten) Portal-Links beim Laden – unabhängig vom veröffentlichten Stand.
const LEGACY_APPLICATION_HREFS = new Set(['https://www.vivaporta.org/contact/', 'https://www.vivaporta.org/contact', 'https://vivaporta.org/contact/', 'https://vivaporta.org/contact']);
const legacyApplicationPortalBases = ['https://viva-porta-platform.production4.adornis.de', 'https://app.vivaporta.org'];

const portalHrefByPath: Record<string, string> = {
  ...Object.fromEntries(industryPageKeys.map(key => [`/basvuru-formu/${key}`, industryApplicationHref(key)])),
  '/basvuru-formu': applicationPortal.overview,
  '/basvuru-formu/pflege': applicationPortal.pflege,
  '/basvuru-formu/gastro': applicationPortal.gastro,
  '/basvuru-formu/handwerk': applicationPortal.handwerk,
  '/basvuru-formu/einzelhandel': applicationPortal.einzelhandel,
};

const rewriteApplicationHref = (href: string): string | null => {
  if (LEGACY_APPLICATION_HREFS.has(href)) return applicationPortal.overview;
  let url: URL;
  try { url = new URL(href, 'https://relative.invalid'); } catch { return null; }
  const withSearch = (destinationHref: string) => {
    const destination = new URL(destinationHref);
    destination.search = url.search;
    destination.hash = url.hash;
    return destination.toString();
  };
  const normalizedHref = (url.origin === 'https://relative.invalid' ? url.pathname : `${url.origin}${url.pathname}`).replace(/\/+$/u, '');
  if (portalHrefByPath[normalizedHref]) return withSearch(portalHrefByPath[normalizedHref]);
  for (const base of legacyApplicationPortalBases) {
    if (!normalizedHref.startsWith(base)) continue;
    const path = normalizedHref.slice(base.length) || '/basvuru-formu';
    return withSearch(portalHrefByPath[path] ?? applicationPortal.overview);
  }

  return null;
};

const rewriteApplicationHrefs = (value: unknown): void => {
  if (Array.isArray(value)) {
    for (const entry of value) rewriteApplicationHrefs(entry);
    return;
  }
  if (!value || typeof value !== 'object') return;
  const record = value as Record<string, unknown>;
  if (typeof record.href === 'string') {
    const nextHref = rewriteApplicationHref(record.href);
    if (nextHref) record.href = nextHref;
  }
  for (const key of Object.keys(record)) rewriteApplicationHrefs(record[key]);
};

export const normalizeLandingEditorProject = (incoming: unknown): LandingEditorProject => {
  incoming = landingEditorProjectInput(incoming);
  const fallback = createDefaultLandingEditorProject();
  if (!isRecord(incoming)) return fallback;
  const incomingContentRevision = typeof incoming.contentRevision === 'number' ? incoming.contentRevision : 0;
  const requiresLegacyContentMigration = incomingContentRevision < 2;
  const requiresSeoContentMigration = incomingContentRevision < 3;
  const requiresGlobusJobsMigration = incomingContentRevision < 5;
  const requiresGlobusMediaMigration = incomingContentRevision < 9;
  const requiresGlobusCompanyCaseStudyMigration = incomingContentRevision < 10;
  const requiresGlobusCampaignMigration = incomingContentRevision < 11;
  const requiresFeaturedGroupPhotoMigration = incomingContentRevision < 12;
  const requiresPresentationRefresh = incomingContentRevision < 13;
  const requiresPhoneNumberMigration = incomingContentRevision < 14;
  const requiresCompanyPlatformHeroCopyRefresh = incomingContentRevision < 17;
  const requiresCompanyHeroActionLinkMigration = incomingContentRevision < 21;

  const incomingContent = isRecord(incoming.content) ? incoming.content : {};
  const content = {} as Record<LandingLocale, LandingContent>;
  let footerOrder = new Map<number, number>();
  for (const locale of landingPageContentRepository.locales) {
    content[locale] = mergeDeep(fallback.content[locale], incomingContent[locale]);
    if (incomingContentRevision < 28) backfillRetailIndustryNavigation(content[locale], fallback.content[locale]);
    if (incomingContentRevision < 29) backfillCareIndustryNavigation(content[locale], fallback.content[locale]);
    if (
      requiresLegacyContentMigration &&
      locale !== 'de' &&
      hasLegacyLocalizedLandingContent(content[locale])
    ) {
      content[locale] = cloneEditorValue(fallback.content[locale]);
    }
    if (requiresLegacyContentMigration) {
      backfillIndustryHeroFan(content[locale], fallback.content[locale]);
      backfillCompanyHeroFan(content[locale], fallback.content[locale]);
      syncFanCardImages(content[locale], fallback.content[locale]);
      normalizeVisibleIndustries(content[locale], fallback.content[locale]);
      backfillFanTestimonials(content[locale], fallback.content[locale]);
      backfillTeamMemberLinks(content[locale], fallback.content[locale]);
      backfillLandingTeamMembers(content[locale], fallback.content[locale]);
      normalizeLaborShortageCopy(content[locale], fallback.content[locale]);
      normalizeMissionLandingCopy(content[locale], fallback.content[locale]);
      normalizePartnerNetworkContent(content[locale], fallback.content[locale]);
      normalizeGlobusPartnerContent(content[locale], fallback.content[locale]);
      normalizeStoryContent(content[locale], fallback.content[locale], fallback.content.de);
      removeProcessMedia(content[locale]);
      softenLegacyQualityClaims(content[locale], fallback.content[locale]);
    }
    if (requiresCompanyPlatformHeroCopyRefresh) refreshCompanyPlatformHeroCopy(content[locale], fallback.content[locale], locale);
    if (incomingContentRevision < 20) refreshCompanyJourneyCopy(content[locale], fallback.content[locale], locale);
    if (requiresCompanyHeroActionLinkMigration) migrateCompanyHeroActionLink(content[locale], fallback.content[locale]);
    refreshMatchingDemoPortraits(content[locale].companyPlatformHero.matching, fallback.content[locale].companyPlatformHero.matching);
    if (content[locale].companyObjections.motion !== 'off') content[locale].companyObjections.motion = 'subtle';
    const incomingLocaleContent = incomingContent[locale];
    if (incomingContentRevision < 22 && (!isRecord(incomingLocaleContent) || !Object.hasOwn(incomingLocaleContent, 'companyPeopleHero'))) migrateCompanyPeopleHeroContent(content[locale], locale);
    if (content[locale].companyPeopleHero.motion !== 'off') content[locale].companyPeopleHero.motion = 'subtle';
    if (['Lerne Enes kennen', 'Meet Enes', 'Enes ile tanış'].includes(content[locale].companyPeopleHero.scrollLabel)) content[locale].companyPeopleHero.scrollLabel = fallback.content[locale].companyPeopleHero.scrollLabel;
    rewriteApplicationHrefs(content[locale]);
    if (requiresSeoContentMigration) {
      content[locale].faqApplicant = cloneEditorValue(fallback.content[locale].faqApplicant);
    }
    if (incomingContentRevision < 19) {
      const order = migrateFooterLinks(content[locale], fallback.content[locale], incomingContentRevision);
      if (locale === 'de') footerOrder = order;
    }
    content[locale].footer.socialLinks = content[locale].footer.socialLinks?.filter(link => !/linkedin/i.test(link.href));
    if (requiresPresentationRefresh) normalizeLandingPresentation(content[locale], fallback.content[locale]);
  }
  const incomingStaticPages = isRecord(incoming.staticPages) ? incoming.staticPages : {};
  const hasLocaleStaticPages = landingPageContentRepository.locales.some(locale => isRecord(incomingStaticPages[locale]));
  const staticPages = {} as LandingEditorProject['staticPages'];
  for (const locale of landingPageContentRepository.locales) {
    staticPages[locale] = {} as Record<StaticPageAudience, Record<StaticPageKey, StaticPageContent>>;
    const localePages = hasLocaleStaticPages
      ? isRecord(incomingStaticPages[locale])
        ? incomingStaticPages[locale]
        : {}
      : locale === 'de'
        ? incomingStaticPages
        : {};
    const looksNestedAudiencePages = staticPageAudiences.some(audience => isRecord(localePages[audience]));
    for (const audience of staticPageAudiences) {
      staticPages[locale][audience] = {} as Record<StaticPageKey, StaticPageContent>;
      const incomingAudiencePages = looksNestedAudiencePages
        ? isRecord(localePages[audience])
          ? localePages[audience]
          : {}
        : audience === 'company'
          ? localePages
          : {};
      for (const pageKey of staticPageKeys) {
        const defaults = fallback.staticPages[locale][audience][pageKey];
        staticPages[locale][audience][pageKey] = mergeDeep(defaults, incomingAudiencePages[pageKey]);
        if (
          requiresLegacyContentMigration ||
          (requiresGlobusJobsMigration && pageKey === 'globus') ||
          (requiresGlobusCompanyCaseStudyMigration && audience === 'company' && pageKey === 'globus') ||
          (requiresGlobusCampaignMigration && pageKey === 'ablauf')
        ) {
          backfillStaticPageDefaults(
            staticPages[locale][audience][pageKey],
            defaults,
            audience,
            pageKey,
          );
        }
        if (requiresGlobusMediaMigration && pageKey === 'globus') {
          backfillGlobusBakeryMedia(staticPages[locale][audience][pageKey], defaults);
          backfillGlobusButcherMedia(staticPages[locale][audience][pageKey], defaults);
          backfillGlobusFreshCounterMedia(staticPages[locale][audience][pageKey], defaults);
          backfillGlobusGastronomyMedia(staticPages[locale][audience][pageKey], defaults);
        }
        if (requiresFeaturedGroupPhotoMigration) {
          backfillFeaturedGroupPhoto(staticPages[locale][audience][pageKey], defaults, pageKey);
        }
        if (requiresPresentationRefresh) {
          normalizeStaticPagePresentation(staticPages[locale][audience][pageKey], defaults, audience, pageKey);
        }
        staticPages[locale][audience][pageKey].title = industryHeroHeading(pageKey, staticPages[locale][audience][pageKey].title, defaults.title, incomingContentRevision, audience);
        staticPages[locale][audience][pageKey].intro = industryHeroIntro(pageKey, staticPages[locale][audience][pageKey].intro, defaults.intro, incomingContentRevision, audience);
        refreshIndustryHeroHighlight(staticPages[locale][audience][pageKey], defaults, locale, incomingContentRevision);
        const contact = staticPages[locale][audience][pageKey].contact;
        if (contact) contact.mapConsent = contactMapConsentCopy(locale, contact.mapConsent);
        applyBranchPortalLinks(staticPages[locale][audience][pageKey], pageKey, locale, audience);
      }
    }
  }

  const normalizedProject: LandingEditorProject = {
    version: 1,
    contentRevision: 29,
    updatedAt: typeof incoming.updatedAt === 'string' ? incoming.updatedAt : fallback.updatedAt,
    publishedAt: typeof incoming.publishedAt === 'string' ? incoming.publishedAt : undefined,
    content,
    staticPages,
    sectionOrder: normalizeSectionOrder(incoming.sectionOrder, typeof incoming.contentRevision === 'number' ? incoming.contentRevision : 0),
    staticSectionOrder: normalizeStaticSectionOrder(incoming.staticSectionOrder),
    staticPageSectionOrder: normalizeStaticPageBuiltInSectionOrderMap(
      incoming.staticPageSectionOrder,
      staticPages.de,
    ),
    elementFrames: normalizeFrames(incoming.elementFrames),
    editorAnchorIds: normalizeLandingEditorCommentAnchorIds(incoming.editorAnchorIds),
    settings: normalizeSettings(incoming.settings),
  };
  if (incomingContentRevision < 22) migrateCompanyPeopleHeroVisibility(normalizedProject.elementFrames);
  if (incomingContentRevision < 19) {
    normalizedProject.elementFrames = remapFooterKeys(normalizedProject.elementFrames, footerOrder);
    normalizedProject.editorAnchorIds = remapFooterKeys(normalizedProject.editorAnchorIds, footerOrder);
  }
  if (requiresPhoneNumberMigration) migrateLegacyPhoneNumber(normalizedProject);
  migrateRetiredLandingLinks(normalizedProject);
  repairInvalidEditorAssets(normalizedProject, fallback);
  refreshBuiltInGlobusJobAssets(normalizedProject, fallback);
  const retained = collectRetainedFields(incoming, normalizedProject, Array.isArray(incoming.retainedFields) ? incoming.retainedFields as RetainedField[] : []);
  if (retained.length) normalizedProject.retainedFields = retained;
  return normalizedProject;
};
export const getOrderedLandingHomeSections = (project: LandingEditorProject): LandingHomeSectionKey[] => normalizeSectionOrder(project.sectionOrder, project.contentRevision);

export const getAudienceLandingHomeSections = (project: LandingEditorProject, audience: StaticPageAudience): LandingHomeSectionKey[] => {
  const order = getOrderedLandingHomeSections(project);
  const blocked = new Set<LandingHomeSectionKey>(
    audience === 'company'
      ? ['hero', 'heroApplicant', 'heroFanApplicant', 'globusCampaignApplicant', 'faqApplicant', 'benefits', 'costs', 'recognition', 'requirements', 'knowledge']
      : ['hero', 'heroApplicant', 'heroCompany', 'heroFanCompany', 'companyPeopleHero', 'companyPlatformHero', 'companyObjections', 'companyMatching', 'globusCampaignCompany', 'faqCompany', 'quality'],
  );
  const audienceHeroKeys = new Set<LandingHomeSectionKey>(audience === 'company' ? landingCompanyHeroSectionKeys : ['globusCampaignApplicant', 'heroFanApplicant']);
  const defaultHero: LandingHomeSectionKey = audience === 'company' ? 'companyPeopleHero' : 'globusCampaignApplicant';
  const faq: LandingHomeSectionKey = audience === 'company' ? 'faqCompany' : 'faqApplicant';
  const normalized = order.filter(section => !blocked.has(section));
  const deduped = normalized.filter((section, index) => normalized.indexOf(section) === index);
  const withHero: LandingHomeSectionKey[] = deduped.some(section => audienceHeroKeys.has(section)) ? deduped : [defaultHero, ...deduped];
  const withFaq: LandingHomeSectionKey[] = withHero.includes(faq) ? withHero : [...withHero, faq];
  return withFaq;
};

export const getOrderedStaticSections = (project: LandingEditorProject, pageKey: StaticPageKey, audience: StaticPageAudience = 'applicant'): LandingHomeSectionKey[] =>
  normalizeStaticSectionList(project.staticSectionOrder?.[audience]?.[pageKey]);

export const getOrderedStaticPageBuiltInSections = (project: LandingEditorProject, pageKey: StaticPageKey, audience: StaticPageAudience = 'applicant'): StaticPageBuiltInSectionKey[] =>
  normalizeStaticPageBuiltInSectionOrder(
    project.staticPageSectionOrder?.[audience]?.[pageKey],
    project.staticPages.de[audience][pageKey],
  );
